import { formatPrice, type MenuGridItemExtraGroup } from "@/components/features/menu/menu-grid"
import { groupRuleSummary, groupSelectionValid } from "@/features/staff/extras-workspace"
import { t } from "@/lib/i18n"
import { cn } from "@/lib/utils"

// How many of the currently-selected extra ids belong to this group. Exported
// so each order surface can compute `extraGroupsValid` (every group satisfied)
// with the same counting rule the selector renders from.
export function selectedExtraCount(group: MenuGridItemExtraGroup, extraIds: string[]): number {
  return extraIds.filter((id) => group.extras.some((extra) => String(extra.id) === id)).length
}

// One section per attached extra group: radio-style when a group caps at one
// selection, checkboxes otherwise, with the group's rule summary and per-extra
// price deltas. Shared verbatim by both staff order-entry surfaces
// (new-order-form, table-detail-workspace) so the selection rules and markup
// can't drift between them. State-agnostic: callers
// own `extraIds` (Inertia form data or local state) and pass `onChange` with
// the next full selection.
export function ExtraGroupSelector({
  groups,
  extraIds,
  onChange,
  currency,
  heading,
  error = null,
}: {
  groups: MenuGridItemExtraGroup[]
  extraIds: string[]
  onChange: (next: string[]) => void
  currency: string
  heading: string
  error?: string | null
}) {
  if (groups.length === 0) return null

  return (
    <div className="space-y-4">
      <p className="text-sm font-medium">{heading}</p>
      {groups.map((group) => {
        const selectedCount = selectedExtraCount(group, extraIds)
        const valid = groupSelectionValid(group, selectedCount)
        const isRadio = group.maxSelect === 1
        // Same cap the diner selector enforces: a multi-select group at its
        // maxSelect freezes its unchecked rows, so staff can't overshoot into a
        // selection that silently fails validation on submit. Radios never hit
        // this (picking one clears its siblings).
        const atCap = !isRadio && group.maxSelect != null && selectedCount >= group.maxSelect
        const groupName = `extra-group-${group.id}`
        const groupExtraIds = group.extras.map((extra) => String(extra.id))
        const clearGroupSelection = () =>
          onChange(extraIds.filter((id) => !groupExtraIds.includes(id)))
        const setGroupSelection = (extraId: string, selected: boolean) => {
          if (isRadio) {
            onChange([
              ...extraIds.filter((id) => !groupExtraIds.includes(id)),
              ...(selected ? [extraId] : []),
            ])
            return
          }
          if (selected && atCap) return
          onChange(selected ? [...extraIds, extraId] : extraIds.filter((id) => id !== extraId))
        }

        return (
          <div key={group.id} className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium">{group.name}</p>
              <span className={cn("text-xs", valid ? "text-muted-foreground" : "text-destructive")}>
                {groupRuleSummary(group)}
              </span>
            </div>
            <div className="space-y-2">
              {isRadio && group.minSelect === 0 && (
                <label className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border bg-card px-4 py-3 shadow-xs hover:bg-muted/30 active:bg-muted/50">
                  <span className="text-sm font-medium text-muted-foreground">
                    {t("ui.react.pages.staff.location.manage.menu.index.extra_group_no_selection")}
                  </span>
                  <input
                    type="radio"
                    name={groupName}
                    className="h-5 w-5 accent-primary"
                    checked={selectedCount === 0}
                    onChange={clearGroupSelection}
                  />
                </label>
              )}
              {group.extras.map((extra) => {
                const checked = extraIds.includes(String(extra.id))
                const soldOut = extra.status === "sold_out"
                const blocked = soldOut || (atCap && !checked)
                return (
                  <label
                    key={extra.id}
                    className={cn(
                      "flex cursor-pointer items-center justify-between gap-3 rounded-lg border bg-card px-4 py-3 shadow-xs hover:bg-muted/30 active:bg-muted/50",
                      blocked && "cursor-not-allowed opacity-60"
                    )}
                  >
                    <span className="text-sm font-medium">{extra.name}</span>
                    <span className="flex shrink-0 items-center gap-3">
                      <span className="text-sm text-muted-foreground">
                        {soldOut
                          ? t("ui.react.components.features.menu.menu_grid.soldout")
                          : Number(extra.price) > 0
                            ? `+${formatPrice(extra.price, currency)}`
                            : t("ui.user.dish.free")}
                      </span>
                      <input
                        type={isRadio ? "radio" : "checkbox"}
                        name={isRadio ? groupName : undefined}
                        className="h-5 w-5 accent-primary"
                        checked={checked}
                        disabled={blocked}
                        onChange={(event) => setGroupSelection(String(extra.id), event.target.checked)}
                      />
                    </span>
                  </label>
                )
              })}
            </div>
          </div>
        )
      })}
      {error ? <p className="text-sm font-medium text-destructive">{error}</p> : null}
    </div>
  )
}
